(() => {
  const { routes, strings } = window.theme;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const announce = (message) => {
    const region = document.getElementById('LiveRegion');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 50);
  };

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
      el.classList.add('is-bumped');
      window.setTimeout(() => el.classList.remove('is-bumped'), 200);
    });
    document.querySelectorAll('[data-cart-count-label]').forEach((el) => {
      el.textContent = el.textContent.replace(/\d+/, count);
    });
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  class ThemeDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.dialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-close]')) this.close();
      });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close(true);
      });
    }

    open(opener) {
      if (!this.dialog || this.dialog.open) return;
      this.opener = opener || document.activeElement;
      this.dialog.showModal();
      document.body.classList.add('is-locked');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.dialog.classList.add('is-open'));
      });
      this.opener?.setAttribute('aria-expanded', 'true');
      document.dispatchEvent(new CustomEvent('drawer:open'));
    }

    close(instant = false) {
      if (!this.dialog || !this.dialog.open || this.closing) return;
      this.closing = true;
      this.dialog.classList.remove('is-open');
      const panel = this.dialog.querySelector('.drawer__panel');
      const finish = () => {
        this.closing = false;
        this.dialog.close();
        document.body.classList.remove('is-locked');
        this.opener?.setAttribute('aria-expanded', 'false');
        this.opener?.focus?.();
        document.dispatchEvent(new CustomEvent('drawer:close'));
      };
      if (instant) {
        finish();
        return;
      }
      if (reduceMotion.matches || !panel) {
        window.setTimeout(finish, 160);
        return;
      }
      let done = false;
      const end = () => { if (!done) { done = true; finish(); } };
      panel.addEventListener('transitionend', end, { once: true });
      window.setTimeout(end, 400);
    }
  }

  class MenuDrawer extends ThemeDrawer {
    connectedCallback() {
      super.connectedCallback();
      const warm = () => this.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      document.querySelectorAll('[data-menu-open]').forEach((button) => {
        button.addEventListener('pointerdown', warm, { once: true });
        button.addEventListener('focus', warm, { once: true });
        button.addEventListener('click', () => this.open(button));
      });
    }
  }

  class CartLines {
    static bind(root, getSectionId, getContainer) {
      let timer;
      root.addEventListener('click', (event) => {
        const step = event.target.closest('[data-qty-step]');
        const remove = event.target.closest('[data-remove]');
        const nudge = event.target.closest('[data-nudge-line]');
        if (nudge) {
          CartLines.change(nudge.dataset.nudgeLine, Number(nudge.dataset.nudgeQty), getSectionId(), getContainer);
          return;
        }
        if (step) {
          const input = step.closest('[data-qty]').querySelector('[data-qty-input]');
          const next = Math.max(0, parseInt(input.value || '0', 10) + parseInt(step.dataset.qtyStep, 10));
          input.value = next;
          CartLines.change(input.dataset.line, next, getSectionId(), getContainer, `[data-qty-input][data-line="${input.dataset.line}"]`);
        }
        if (remove) {
          CartLines.change(remove.dataset.line, 0, getSectionId(), getContainer, null, true);
        }
      });
      root.addEventListener('change', (event) => {
        const input = event.target.closest('[data-qty-input]');
        if (!input) return;
        window.clearTimeout(timer);
        timer = window.setTimeout(() => {
          const value = Math.max(0, parseInt(input.value || '0', 10));
          CartLines.change(input.dataset.line, value, getSectionId(), getContainer, `[data-qty-input][data-line="${input.dataset.line}"]`);
        }, 250);
      });
    }

    static async change(line, quantity, sectionId, container, focusSelector, removed = false) {
      const contents = container();
      contents?.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(line), quantity, sections: [sectionId], sections_url: window.location.pathname })
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          const error = contents?.querySelector('[data-cart-error]');
          if (error) error.textContent = data.description || strings.cartError;
          contents?.removeAttribute('aria-busy');
          return;
        }
        CartLines.render(data.sections[sectionId], container);
        updateCartCount(data.item_count);
        announce(removed || quantity === 0 ? strings.itemRemoved : strings.cartUpdated);
        const fresh = container();
        const target = (focusSelector && fresh?.querySelector(focusSelector)) || fresh?.querySelector('[data-qty-input], .button, h1, h2');
        target?.focus?.({ preventScroll: true });
      } catch (e) {
        const error = contents?.querySelector('[data-cart-error]');
        if (error) error.textContent = strings.cartError;
        contents?.removeAttribute('aria-busy');
      }
    }

    static render(html, container) {
      if (!html) return;
      const doc = parseHTML(html);
      const next = doc.querySelector('[data-cart-contents]');
      const current = container();
      if (next && current) current.replaceWith(next);
    }
  }

  class CartDrawer extends ThemeDrawer {
    connectedCallback() {
      super.connectedCallback();
      if (!this.dialog) return;
      this.sectionId = this.dialog.dataset.sectionId || 'cart-drawer';
      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-cart-open]');
        if (!opener) return;
        event.preventDefault();
        this.open(opener);
      });
      CartLines.bind(this.dialog, () => this.sectionId, () => this.dialog.querySelector('[data-cart-contents]'));
    }

    renderFromSections(sections) {
      CartLines.render(sections?.[this.sectionId], () => this.dialog.querySelector('[data-cart-contents]'));
    }
  }

  class CartPage extends HTMLElement {
    connectedCallback() {
      const sectionId = this.dataset.sectionId;
      CartLines.bind(this, () => sectionId, () => this.querySelector('[data-cart-contents]'));
    }
  }

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.formId = this.form.getAttribute('id');
      this.button = this.form.querySelector('[data-add-button]');
      this.label = this.form.querySelector('[data-add-label]');
      this.error = this.form.querySelector('[data-form-error]');
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.form.addEventListener('change', (event) => this.onChange(event));
      this.variants = JSON.parse(this.form.querySelector('[data-variants]')?.textContent || 'null');
    }

    setBusy(busy) {
      document.querySelectorAll(`[form="${this.formId}"][data-sticky-button]`).forEach((b) => b.setAttribute('aria-busy', busy));
      this.button.setAttribute('aria-busy', busy);
      if (busy) this.label.textContent = strings.adding;
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.button.disabled || this.button.getAttribute('aria-busy') === 'true') return;
      this.error.textContent = '';
      this.setBusy(true);
      const drawer = document.querySelector('cart-drawer');
      const formData = new FormData(this.form);
      const body = {
        items: [{ id: Number(formData.get('id')), quantity: Number(formData.get('quantity') || 1) }],
        sections: drawer ? [drawer.sectionId] : [],
        sections_url: window.location.pathname
      };
      try {
        const response = await fetch(`${routes.cartAdd}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          this.error.textContent = data.description || strings.cartError;
          this.setBusy(false);
          this.label.textContent = strings.addToCart;
          return;
        }
        const cart = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        updateCartCount(cart.item_count);
        this.setBusy(false);
        this.label.textContent = strings.added;
        announce(strings.added);
        window.setTimeout(() => { this.label.textContent = strings.addToCart; }, 1600);
        if (drawer && drawer.dialog) {
          this.closest('side-panel')?.close(true);
          drawer.renderFromSections(data.sections);
          drawer.open(this.button);
        } else {
          window.location.href = routes.cart;
        }
      } catch (e) {
        this.error.textContent = strings.cartError;
        this.setBusy(false);
        this.label.textContent = strings.addToCart;
      }
    }

    onChange(event) {
      if (event.target.name === 'quantity') {
        const tier = event.target.closest('.tier');
        const total = tier?.querySelector('[data-tier-total]');
        const sticky = document.querySelector(`sticky-buy[data-form-id="${this.formId}"] [data-sticky-price]`);
        if (sticky && total) sticky.textContent = total.lastChild.textContent.trim();
        return;
      }
      if (!this.variants || !event.target.dataset.optionIndex) return;
      const selected = Array.from(this.form.querySelectorAll('[data-option-index]:checked')).map((i) => i.value);
      const variant = this.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      const available = Boolean(variant && variant.available);
      if (variant) this.form.querySelector('[data-variant-input]').value = variant.id;
      this.button.disabled = !available;
      this.label.textContent = !variant ? strings.unavailable : available ? strings.addToCart : strings.soldOut;
      document.querySelectorAll(`[form="${this.formId}"][data-sticky-button]`).forEach((b) => {
        b.disabled = !available;
        b.textContent = this.label.textContent;
      });
      const pickedLabel = this.form.querySelector(`[data-picked-option="${event.target.dataset.optionIndex}"]`);
      if (pickedLabel) pickedLabel.textContent = event.target.value;
      if (variant) this.updateVariantDisplay(variant);
    }

    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'nl', { style: 'currency', currency: window.theme.currency || 'EUR' }).format(cents / 100).replace(/^([^\d\s-]+)\s/, '$1');
    }

    updateVariantDisplay(variant) {
      const scope = this.closest('[data-product-section], .split__body, .landing-hero__body') || document;
      scope.querySelectorAll('.price[data-price]').forEach((el) => { el.textContent = this.money(variant.price); });
      const sticky = document.querySelector(`sticky-buy[data-form-id="${this.formId}"] [data-sticky-price]`);
      if (sticky) sticky.textContent = this.money(variant.price);
      const tiers = this.form.querySelector('[data-tiers]');
      if (tiers) {
        tiers.querySelectorAll('input[name="quantity"]').forEach((input) => {
          const qty = Number(input.value);
          const pct = Number(input.dataset.tierPct || 0);
          const full = variant.price * qty;
          const total = full - Math.floor((full * pct) / 100);
          const box = input.closest('.tier').querySelector('[data-tier-total]');
          if (!box) return;
          const regular = box.querySelector('s');
          box.textContent = '';
          if (regular && pct > 0) {
            regular.lastChild.textContent = this.money(full);
            box.append(regular);
          }
          box.append(document.createTextNode(this.money(total)));
        });
      }
      const availability = scope.querySelector('[data-availability]');
      if (availability) {
        const state = !variant.available ? 'soldout' : variant.backorder ? 'backorder' : 'instock';
        availability.className = `availability availability--${state}`;
        const text = availability.querySelector('span:last-child');
        if (text) {
          const strong = document.createElement('strong');
          strong.textContent = state === 'soldout' ? strings.soldOut : state === 'backorder' ? strings.backorder : strings.inStock;
          text.replaceChildren(strong);
          if (state === 'backorder') text.append(document.createTextNode(` – ${strings.backorderInfo}`));
        }
      }
      if (variant.media) {
        const slide = document.querySelector(`[data-media-id="${variant.media}"]`);
        const track = slide?.closest('[data-gallery-track]');
        if (slide && track && track.scrollWidth > track.clientWidth) {
          track.scrollTo({ left: slide.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        }
      }
    }
  }

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      if (!this.track || this.dots.length === 0) return;
      const slides = Array.from(this.track.querySelectorAll('[data-slide]'));
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
            const index = slides.indexOf(entry.target);
            this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
          }
        });
      }, { root: this.track, threshold: [0.6] });
      slides.forEach((slide) => observer.observe(slide));
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const slide = slides[Number(dot.dataset.dot)];
          this.track.scrollTo({ left: slide.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        });
      });
    }
  }

  class SidePanel extends ThemeDrawer {}

  class ImageZoom extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.img = this.querySelector('[data-zoom-img]');
      if (!this.dialog || !this.img) return;
      document.addEventListener('click', (event) => {
        const button = event.target.closest('[data-zoom]');
        if (!button) return;
        this.opener = button;
        this.img.src = button.dataset.zoom;
        this.img.alt = button.dataset.zoomAlt || '';
        this.dialog.showModal();
        document.body.classList.add('is-locked');
        requestAnimationFrame(() => this.dialog.classList.add('is-open'));
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog || event.target.closest('[data-close]')) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => {
        this.dialog.classList.remove('is-open');
        document.body.classList.remove('is-locked');
        this.opener?.focus();
      });
    }
  }

  class JarReveal extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('.jar__button');
      this.list = this.querySelector('.jar__list');
      this.hint = this.querySelector('[data-jar-hint]');
      this.label = this.querySelector('[data-jar-label]');
      if (!this.button || !this.list) return;
      this.list.inert = true;
      this.list.setAttribute('aria-hidden', 'true');
      this.button.addEventListener('click', () => this.toggle());
    }

    toggle() {
      const open = !this.classList.contains('is-open');
      this.classList.toggle('is-open', open);
      this.button.setAttribute('aria-expanded', String(open));
      this.list.inert = !open;
      this.list.setAttribute('aria-hidden', String(!open));
      if (this.hint) this.hint.textContent = open ? this.dataset.hintOpen : this.dataset.hintClosed;
      if (this.label) this.label.textContent = open ? this.dataset.labelOpen : this.dataset.labelClosed;
    }
  }

  class PotCalculator extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('[data-calc-range]');
      if (!this.range) return;
      this.servings = Number(this.dataset.servings);
      this.price = Number(this.dataset.price);
      this.bestPct = Number(this.dataset.bestPct);
      this.format = new Intl.NumberFormat(document.documentElement.lang || 'nl', { style: 'currency', currency: this.dataset.currency || 'EUR' });
      this.number = new Intl.NumberFormat(document.documentElement.lang || 'nl', { maximumFractionDigits: 1 });
      this.range.addEventListener('input', () => this.update());
      this.update();
    }

    money(cents) { return this.format.format(cents / 100).replace(/\u00a0/g, ''); }

    update() {
      const sessions = Number(this.range.value);
      const weeks = this.servings / sessions;
      this.querySelector('[data-calc-sessions]').textContent = sessions === 1 ? this.dataset.sessionsOne : this.dataset.sessions.replace('[n]', sessions);
      this.querySelector('[data-calc-weeks]').textContent = this.dataset.weeks.replace('[w]', this.number.format(weeks));
      this.querySelector('[data-calc-per]').textContent = this.dataset.per.replace('[p]', this.money(Math.round(this.price / this.servings)));
      const best = this.querySelector('.calc__best');
      if (best && this.bestPct > 0) {
        const full = this.price * 3;
        const total = full - Math.floor(full * this.bestPct / 100);
        best.textContent = this.dataset.best.replace('[p]', this.money(Math.round(total / 3 / this.servings)));
      }
    }
  }

  class StickyBuy extends HTMLElement {
    connectedCallback() {
      this.target = document.querySelector('[data-main-buy] [data-add-button]') || document.querySelector('[data-main-buy]');
      this.button = this.querySelector('[data-sticky-button]');
      if (!this.target) return;
      this.pastBuy = false;
      this.suppressed = false;
      /* alleen tonen als je voorbij de koopknop bent (niet ervoor, in de hero) */
      new IntersectionObserver(([entry]) => {
        this.pastBuy = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.update();
      }).observe(this.target);
      /* verbergen zolang een ander koopmoment in beeld is (bijv. de slotsectie) */
      this.hiders = new Set();
      const hideIo = new IntersectionObserver((entries) => {
        entries.forEach((e) => (e.isIntersecting ? this.hiders.add(e.target) : this.hiders.delete(e.target)));
        this.update();
      });
      document.querySelectorAll('[data-sticky-hide]').forEach((el) => hideIo.observe(el));
      document.addEventListener('drawer:open', () => { this.suppressed = true; this.update(); });
      document.addEventListener('drawer:close', () => { this.suppressed = false; this.update(); });
      document.addEventListener('focusin', (event) => {
        if (event.target.matches('input:not([type=radio]), textarea, select')) { this.typing = true; this.update(); }
      });
      document.addEventListener('focusout', () => { this.typing = false; this.update(); });
      this.bannerCheck = window.setInterval(() => this.update(), 1000);
      let ticking = false;
      window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => { ticking = false; this.update(); });
      }, { passive: true });
    }

    cookieBannerVisible() {
      const banner = document.getElementById('shopify-pc__banner');
      if (!banner) return false;
      const style = window.getComputedStyle(banner);
      return style.display !== 'none' && style.visibility !== 'hidden' && banner.getBoundingClientRect().height > 0;
    }

    update() {
      /* positie live bepalen: ook na een directe sprong (anker, terug naar boven) klopt de stand */
      this.pastBuy = this.target.getBoundingClientRect().bottom < 0;
      const visible = this.pastBuy && !this.suppressed && !this.typing && !(this.hiders && this.hiders.size) && !this.cookieBannerVisible();
      this.classList.toggle('is-visible', visible);
      this.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if (this.button) this.button.tabIndex = visible ? 0 : -1;
    }

    disconnectedCallback() { window.clearInterval(this.bannerCheck); }
  }

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      const update = () => this.classList.toggle('is-scrolled', window.scrollY > 4);
      window.addEventListener('scroll', update, { passive: true });
      update();
    }
  }

  class SignupForm extends HTMLElement {
    connectedCallback() {
      const form = this.querySelector('form');
      form?.addEventListener('submit', (event) => {
        const trap = form.querySelector('[data-trap]');
        if (trap && trap.value.trim() !== '') event.preventDefault();
      });
      const base = this.querySelector('input[type="hidden"][name="contact[tags]"]');
      if (!base) return;
      const picked = this.querySelector('[data-picked]');
      this.addEventListener('change', (event) => {
        const radio = event.target;
        if (!radio.matches('input[type="radio"][name="contact[tags]"]')) return;
        base.disabled = true;
        if (!picked) return;
        const color = radio.closest('.flavour')?.style.getPropertyValue('--flavour');
        const dot = document.createElement('span');
        dot.className = 'signup__picked-dot';
        dot.setAttribute('aria-hidden', 'true');
        if (color) dot.style.setProperty('--dot', color);
        const label = document.createElement('span');
        label.textContent = `${picked.dataset.label} `;
        const name = document.createElement('strong');
        name.textContent = radio.dataset.flavourName || '';
        label.append(name);
        picked.replaceChildren(dot, label);
      });
    }
  }

  class AoVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.toggle = this.querySelector('[data-video-toggle]');
      if (!this.video) return;
      this.video.muted = true;
      this.video.playsInline = true;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      const sync = () => {
        const paused = this.video.paused;
        this.classList.toggle('is-paused', paused);
        if (this.toggle) this.toggle.setAttribute('aria-label', paused ? this.toggle.dataset.play : this.toggle.dataset.pause);
      };
      this.video.addEventListener('play', sync);
      this.video.addEventListener('pause', sync);
      this.toggle?.addEventListener('click', () => (this.video.paused ? this.video.play() : this.video.pause()));
      if (!reduce.matches) this.video.play().catch(() => {});
      sync();
    }
  }

  customElements.define('signup-form', SignupForm);
  customElements.define('ao-video', AoVideo);
  customElements.define('menu-drawer', MenuDrawer);
  customElements.define('cart-drawer', CartDrawer);
  customElements.define('cart-page', CartPage);
  customElements.define('product-form', ProductForm);
  customElements.define('media-gallery', MediaGallery);
  customElements.define('sticky-buy', StickyBuy);
  customElements.define('side-panel', SidePanel);
  customElements.define('image-zoom', ImageZoom);
  customElements.define('jar-reveal', JarReveal);
  customElements.define('pot-calculator', PotCalculator);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-panel-open]');
    if (!opener) return;
    const panel = document.getElementById(opener.dataset.panelOpen);
    if (!panel || typeof panel.open !== 'function') return;
    event.preventDefault();
    panel.open(opener);
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-quick-view]');
    if (!button) return;
    const panel = document.getElementById('QuickView');
    const url = button.dataset.quickView;
    if (!panel || typeof panel.open !== 'function') return;
    event.preventDefault();
    const body = panel.querySelector('[data-quick-body]');
    body.setAttribute('aria-busy', 'true');
    body.innerHTML = `<p class="quick__loading">${strings.loading}</p>`;
    panel.open(button);
    try {
      const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}view=quick`);
      if (!response.ok) throw new Error(response.status);
      body.innerHTML = await response.text();
    } catch (error) {
      window.location.href = url;
    } finally {
      body.removeAttribute('aria-busy');
    }
  });
  customElements.define('sticky-header', StickyHeader);

  document.querySelector('[data-focus-on-load]')?.focus();

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-focus-target]');
    if (!link) return;
    const target = document.getElementById(link.dataset.focusTarget);
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
    target.focus({ preventScroll: true });
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-share]');
    if (!button) return;
    const url = window.location.origin + window.location.pathname;
    const data = { title: button.dataset.shareTitle, text: button.dataset.shareText, url };
    try {
      if (navigator.share) {
        await navigator.share(data);
      } else {
        await navigator.clipboard.writeText(`${data.text} ${url}`);
        button.textContent = button.dataset.copied;
        announce(button.dataset.copied);
      }
    } catch (e) {}
  });

  const cookieLinks = document.querySelectorAll('[data-cookie-preferences]');
  const enableCookieLinks = () => {
    if (typeof window.privacyBanner?.showPreferences !== 'function') return false;
    cookieLinks.forEach((item) => {
      if (!item.hidden) return;
      item.hidden = false;
      item.querySelector('button').addEventListener('click', () => window.privacyBanner.showPreferences());
    });
    return true;
  };
  if (cookieLinks.length && !enableCookieLinks()) {
    let tries = 0;
    const poll = window.setInterval(() => {
      tries += 1;
      if (enableCookieLinks() || tries > 10) window.clearInterval(poll);
    }, 500);
  }
})();
